import React, { useEffect, useMemo, useState } from "react";
import {
  CCallout,
  CDateRangePicker,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CMultiSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSmartPagination,
  CSmartTable,
  CStepper,
} from "@coreui/react-pro";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import EmployerStatusBand from "../../components/employer/EmployerStatusBand";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

function FilingDisclosure({ title, subtitle, defaultOpen = false, children }) {
  return (
    <details open={defaultOpen} className="rounded-lg border border-slate-200 bg-slate-50">
      <summary className="cursor-pointer list-none px-4 py-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{title}</p>
            {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          <span className="text-xs uppercase tracking-[0.16em] text-slate-400">Details anzeigen</span>
        </div>
      </summary>
      <div className="border-t border-slate-200 px-4 py-4">{children}</div>
    </details>
  );
}

function formatOwnerRoleLabel(value) {
  const labels = {
    employer: "Arbeitgeber",
    candidate: "Bewerber",
    authority: "Behoerde",
    third_party: "Dritter",
    internal: "Intern",
    both: "Gemeinsam",
    manual: "Manuell",
  };
  return labels[value] || value || "Ungeklaert";
}

function formatReviewBucketLabel(value) {
  const labels = {
    ready_to_sign: "Zur Unterschrift bereit",
    ready_to_upload: "Zur Einreichung bereit",
    prepared_but_incomplete: "Vorbereitet, aber unvollstaendig",
    manual_only: "Manuell erforderlich",
    missing_docs: "Fehlt noch",
  };
  return labels[value] || value || "In Arbeit";
}

function formatFillabilityClass(value) {
  const labels = {
    fillable_pdf: "Direkt vorbereitbar",
    structured_transfer_only: "Transferhilfe",
    manual_only: "Manuell erforderlich",
    reference: "Nur Referenz",
  };
  return labels[value] || value || "Unbekannt";
}

function formatRequestReadableState(requestEntry, formatRequestStatus) {
  if (!requestEntry) return "Offen";
  if (requestEntry.status === "fulfilled") return "Erledigt";
  if (requestEntry.status === "obsolete") return "Nicht mehr relevant";
  if (requestEntry.status === "expired") return "Abgelaufen";
  if (requestEntry.awaiting_employer_review || ["response_received", "response_under_review", "response_incomplete", "response_rejected"].includes(requestEntry.response_status)) {
    return "In Pruefung";
  }
  if (requestEntry.overdue) return "Ueberfaellig";
  if (requestEntry.due_soon) return "Bald faellig";
  if (requestEntry.status === "viewed") return "Vom Bewerber gesehen";
  if (requestEntry.status === "sent") return "Warten auf Rueckmeldung";
  if (requestEntry.status === "answered") return "Antwort eingegangen";
  return formatRequestStatus(requestEntry.status);
}

function formatRequestNextStep(requestEntry) {
  if (!requestEntry) return "Keine direkte Aktion offen.";
  if (requestEntry.status === "draft") return "Anfrage senden.";
  if (requestEntry.status === "fulfilled") return "Fuer dieses Thema ist aktuell nichts mehr offen.";
  if (requestEntry.status === "obsolete") return "Keine weitere Bearbeitung noetig.";
  if (requestEntry.awaiting_employer_review || ["response_received", "response_under_review"].includes(requestEntry.response_status)) {
    return "Rueckmeldung pruefen und freigeben.";
  }
  if (requestEntry.response_status === "response_incomplete") return "Fehlende Unterlagen erneut beim Bewerber anfordern.";
  if (requestEntry.response_status === "response_rejected") return "Nacharbeit erneut beim Bewerber anstossen.";
  if (requestEntry.overdue) return "Rueckmeldung des Bewerbers aktiv nachhalten.";
  return "Rueckmeldung des Bewerbers abwarten.";
}

function needsCandidateFollowUpReminder(requestEntry) {
  return ["response_incomplete", "response_rejected"].includes(String(requestEntry?.response_status || "").toLowerCase());
}

function canSendCandidateReminder(requestEntry) {
  if (!requestEntry) return false;
  const status = String(requestEntry.status || "").toLowerCase();
  return ["sent", "viewed"].includes(status) || needsCandidateFollowUpReminder(requestEntry);
}

function getCandidateReminderLabel(requestEntry) {
  return needsCandidateFollowUpReminder(requestEntry) ? "Erneut anfordern" : "Erinnerung senden";
}

function formatDocumentResponsibility(documentEntry, missingEntry) {
  if (missingEntry?.responsibility?.label) return missingEntry.responsibility.label;
  if (documentEntry?.candidate_document_dependency && documentEntry?.employer_document_dependency) return "Bewerber und Arbeitgeber";
  if (documentEntry?.candidate_document_dependency) return "Bewerber";
  if (documentEntry?.employer_document_dependency) return "Arbeitgeber";
  if (documentEntry?.signature_required) return "Arbeitgeber";
  return "Im Paket";
}

function formatDocumentAction(documentEntry, missingEntry) {
  if (missingEntry?.responsibility?.detail) return missingEntry.responsibility.detail;
  if (documentEntry?.review_bucket === "ready_to_sign") return "Jetzt unterschreiben und bereitlegen.";
  if (documentEntry?.review_bucket === "ready_to_upload") return "Zur Einreichung bereithalten.";
  if (documentEntry?.review_bucket === "missing_docs") return "Fehlende Unterlage oder Angabe anfordern.";
  if (documentEntry?.review_bucket === "manual_only") return "Manuell beschaffen oder ausfuellen.";
  if (documentEntry?.fillability_class === "structured_transfer_only") return "Transferhilfe fuer das Original nutzen.";
  return documentEntry?.review_note || documentEntry?.fillability_reason || "Unterlage im Paketstand pruefen.";
}

function isBackgroundReferenceDocument(documentEntry) {
  if (!documentEntry) return false;
  if (documentEntry.fillability_class === "reference" || documentEntry.usable_mode === "reference") return true;
  return ["guide", "visa", "authority_page"].includes(documentEntry.category);
}

function isCandidateFollowupTask(task = {}) {
  if (!task) return false;
  if (task.requires_candidate_input || task.owner_role === "candidate") return true;
  if (String(task.source_bucket || "").toLowerCase() === "missing_docs") {
    if (String(task.action_label || "").toLowerCase().includes("fehlende unterlagen anfordern")) return true;
    if (String(task.title || "").toLowerCase().includes("des bewerbers")) return true;
    if (String(task.description || "").toLowerCase().includes("fehlende unterlagen anfordern")) return true;
  }
  return false;
}

function resolveTaskRequest(task, activeRequests = []) {
  if (!task) return null;
  return (
    activeRequests.find((entry) => Number(entry.task_id || 0) === Number(task.task_id || 0))
    || activeRequests.find((entry) => entry.document_id && task.document_id && String(entry.document_id) === String(task.document_id))
    || null
  );
}

const REQUEST_EDITOR_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const REQUEST_EDITOR_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

function escapeEditorHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeRequestMessageHtml(value) {
  const raw = String(value || "").trim();
  if (!raw || !stripHtml(raw)) return "";
  return raw;
}

function stripRequestLabelPrefix(value) {
  return String(value || "")
    .replace(/^Bitte\s+Request:\s*/i, "")
    .replace(/^Request:\s*/i, "")
    .trim();
}

function buildFallbackRequestSubject(requestEntry) {
  return String(
    requestEntry?.message_subject
    || requestEntry?.title
    || "Unterlagen nachreichen"
  ).trim();
}

function buildFallbackRequestMessageHtml(requestEntry) {
  const existing = normalizeRequestMessageHtml(requestEntry?.message_html);
  if (existing) return existing;
  const title = escapeEditorHtml(stripRequestLabelPrefix(requestEntry?.title || "die angefragte Unterlage"));
  const detail = escapeEditorHtml(String(requestEntry?.description || requestEntry?.request_reason || "").trim());
  return [
    `<p>bitte reiche fuer deinen laufenden Fall <strong>${title}</strong> nach.</p>`,
    detail ? `<p>${detail}</p>` : "",
    `<p>Wenn dir etwas fehlt oder unklar ist, gib bitte direkt Rueckmeldung im Portal.</p>`,
  ].filter(Boolean).join("");
}

function buildRequestComposerSeed(requestEntry) {
  return {
    subject: buildFallbackRequestSubject(requestEntry),
    html: buildFallbackRequestMessageHtml(requestEntry),
  };
}

function normalizeComparableDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

function matchesDateRange(value, startDate, endDate) {
  const comparable = normalizeComparableDate(value);
  if (!comparable) return !(startDate || endDate);
  const start = normalizeComparableDate(startDate);
  const end = normalizeComparableDate(endDate);
  if (start && comparable < start) return false;
  if (end && comparable > end) return false;
  return true;
}

export default function StbAuthorityFilingEmployerWorkspace(props) {
  const {
    row,
    filingData,
    detailState,
    requestedRequestId,
    trainingGuidance,
    activeTab,
    onTabChange,
    onExportKit,
    onFinalizeSubmissionPack,
    onRunPackAction,
    onDownloadPreparedAsset,
    onDownloadAuthorityDocument,
    onUpdateAuthorityTask,
    onCreateCandidateRequest,
    onUpdateCandidateRequest,
    onTaskNoteChange,
    getDeadlineTone,
    getFreshnessMeta,
    getResponsibilityMeta,
    getReviewBucketMeta,
    formatCoverageLevel,
    formatDateTime,
    formatDelta,
    formatHistoryAction,
    formatRequestStatus,
    formatResponseStatus,
    formatTaskStatus,
    formatUrgencyLabel,
  } = props;

  const documents = filingData.documents || [];
  const operativeDocuments = documents.filter((entry) => !isBackgroundReferenceDocument(entry));
  const backgroundReferenceDocuments = documents.filter((entry) => isBackgroundReferenceDocument(entry));
  const missingDocuments = filingData.missing_documents || [];
  const missingById = new Map(missingDocuments.map((entry) => [String(entry.document_id), entry]));
  const openTasks = filingData.task_board?.open_tasks || [];
  const openEmployerTask = openTasks.find((task) => task.owner_role === "employer") || openTasks[0] || null;
  const allRequests = filingData.candidate_request_board?.all_requests || [];
  const activeRequests = allRequests.filter((entry) => !["fulfilled", "obsolete", "expired"].includes(entry.status));
  const openEmployerTaskRequest = openEmployerTask
    ? (
      activeRequests.find((entry) => Number(entry.task_id || 0) === Number(openEmployerTask.task_id || 0))
      || activeRequests.find((entry) => entry.document_id && openEmployerTask.document_id && String(entry.document_id) === String(openEmployerTask.document_id))
      || null
    )
    : null;
  const openCandidateRequest = activeRequests.find((entry) => !entry.awaiting_employer_review) || activeRequests[0] || null;
  const primaryBlocker = filingData.completion_workflow?.next_blockers?.[0]
    || activeRequests.find((entry) => entry.overdue)
    || openTasks.find((task) => task.status === "blocked")
    || null;
  const preparedDocumentCount = operativeDocuments.filter((entry) => entry.review_bucket !== "missing_docs").length;
  const openPointCount = (filingData.task_board?.open_count || 0) + (filingData.candidate_request_board?.active_requests?.length || 0);
  const requestGroups = {
    open: allRequests.filter((entry) => !["fulfilled", "obsolete", "expired"].includes(entry.status) && !entry.awaiting_employer_review),
    review: allRequests.filter((entry) => entry.awaiting_employer_review || ["response_received", "response_under_review", "response_incomplete", "response_rejected"].includes(entry.response_status)),
    done: allRequests.filter((entry) => ["fulfilled", "obsolete", "expired"].includes(entry.status)),
  };
  const packOps = filingData.pack_ops_state || {};
  const latestPack = filingData.final_submission_packs?.[0] || null;
  const currentPack = packOps.current_pack || latestPack;
  const [requestStatusFilter, setRequestStatusFilter] = useState([]);
  const [documentBucketFilter, setDocumentBucketFilter] = useState([]);
  const [requestDeadlineRange, setRequestDeadlineRange] = useState({ startDate: null, endDate: null });
  const [requestQueuePage, setRequestQueuePage] = useState(1);
  const [historyQueuePage, setHistoryQueuePage] = useState(1);
  const [selectedRequestId, setSelectedRequestId] = useState(() => String(requestedRequestId || ""));
  const [requestDrawerVisible, setRequestDrawerVisible] = useState(() => Boolean(requestedRequestId));
  const [requestDraftsById, setRequestDraftsById] = useState({});
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  const [workspaceStepperLayout, setWorkspaceStepperLayout] = useState(() => (typeof window !== "undefined" && window.innerWidth >= 1280 ? "horizontal" : "vertical"));
  const workspaceStepperSteps = useMemo(
    () => ([
      { key: "candidate", label: "Kandidatenanforderungen" },
      { key: "documents", label: "Dokumente" },
      { key: "pack", label: "Pack-Reife" },
      { key: "history", label: "Verlauf" },
    ]),
    [],
  );
  const activeWorkspaceStepNumber = useMemo(() => {
    const activeIndex = workspaceStepperSteps.findIndex((step) => step.key === activeTab);
    return activeIndex >= 0 ? activeIndex + 1 : 1;
  }, [activeTab, workspaceStepperSteps]);
  const requestStatusOptions = useMemo(() => {
    const seen = new Set();
    return allRequests
      .map((entry) => String(entry.status || "").trim())
      .filter((status) => status && !seen.has(status) && seen.add(status))
      .map((status) => ({ value: status, label: formatRequestStatus(status) }));
  }, [allRequests, formatRequestStatus]);
  const documentBucketOptions = useMemo(() => {
    const seen = new Set();
    return operativeDocuments
      .map((entry) => String(entry.review_bucket || "manual_only").trim())
      .filter((bucket) => bucket && !seen.has(bucket) && seen.add(bucket))
      .map((bucket) => ({ value: bucket, label: formatReviewBucketLabel(bucket) }));
  }, [operativeDocuments]);
  const filteredRequestGroups = useMemo(() => {
    const matches = (entry) => {
      if (requestStatusFilter.length && !requestStatusFilter.includes(String(entry.status))) return false;
      const referenceDate = entry.due_at || entry.last_activity_at || null;
      return matchesDateRange(referenceDate, requestDeadlineRange.startDate, requestDeadlineRange.endDate);
    };
    return {
      open: requestGroups.open.filter(matches),
      review: requestGroups.review.filter(matches),
      done: requestGroups.done.filter(matches),
    };
  }, [requestDeadlineRange.endDate, requestDeadlineRange.startDate, requestGroups, requestStatusFilter]);
  const filteredOperativeDocuments = useMemo(
    () => operativeDocuments.filter((entry) => !documentBucketFilter.length || documentBucketFilter.includes(String(entry.review_bucket || "manual_only"))),
    [documentBucketFilter, operativeDocuments],
  );
  const requestQueueRows = useMemo(
    () => ([
      ...filteredRequestGroups.open.map((entry) => ({
        id: `request-open-${entry.request_id}`,
        bucket: "Offen",
        title: entry.title,
        status: formatRequestReadableState(entry, formatRequestStatus),
        dueAt: formatDateTime(entry.due_at),
        lastActivity: formatDateTime(entry.last_activity_at),
        urgency: formatUrgencyLabel(entry.urgency),
        nextStep: formatRequestNextStep(entry),
        tone: getDeadlineTone(entry),
        requestEntry: entry,
      })),
      ...filteredRequestGroups.review.map((entry) => ({
        id: `request-review-${entry.request_id}`,
        bucket: "In Pruefung",
        title: entry.title,
        status: formatRequestReadableState(entry, formatRequestStatus),
        dueAt: formatDateTime(entry.due_at),
        lastActivity: formatDateTime(entry.last_activity_at),
        urgency: formatUrgencyLabel(entry.urgency),
        nextStep: formatRequestNextStep(entry),
        tone: getDeadlineTone(entry),
        requestEntry: entry,
      })),
      ...filteredRequestGroups.done.map((entry) => ({
        id: `request-done-${entry.request_id}`,
        bucket: "Erledigt",
        title: entry.title,
        status: formatRequestReadableState(entry, formatRequestStatus),
        dueAt: formatDateTime(entry.due_at),
        lastActivity: formatDateTime(entry.last_activity_at),
        urgency: formatUrgencyLabel(entry.urgency),
        nextStep: formatRequestNextStep(entry),
        tone: getDeadlineTone(entry),
        requestEntry: entry,
      })),
    ]),
    [filteredRequestGroups, formatRequestStatus, getDeadlineTone],
  );
  const filingHistoryRows = useMemo(
    () => (filingData.history || []).map((entry) => ({
      id: `history-${entry.case_id}-${entry.version_no}`,
      version: `Version ${entry.version_no}`,
      action: formatHistoryAction(entry.action_type),
      stage: entry.history_summary?.current_pack_stage || entry.history_summary?.current_pack_freeze_state || "k.A.",
      openPoints: `${entry.history_summary?.open_tasks_count || 0} Aufgaben / ${entry.history_summary?.active_requests_count || 0} Requests`,
      packs: `${entry.history_summary?.final_pack_count || 0}`,
      createdAt: formatDateTime(entry.created_at),
      entry,
    })),
    [filingData.history, formatHistoryAction, formatDateTime],
  );
  const requestQueuePageSize = 6;
  const historyQueuePageSize = 5;
  const requestQueuePageCount = Math.max(1, Math.ceil(requestQueueRows.length / requestQueuePageSize));
  const historyQueuePageCount = Math.max(1, Math.ceil(filingHistoryRows.length / historyQueuePageSize));
  const pagedRequestQueueRows = requestQueueRows.slice((requestQueuePage - 1) * requestQueuePageSize, requestQueuePage * requestQueuePageSize);
  const pagedHistoryQueueRows = filingHistoryRows.slice((historyQueuePage - 1) * historyQueuePageSize, historyQueuePage * historyQueuePageSize);
  const activeRequestRow = useMemo(
    () => requestQueueRows.find((entry) => String(entry.requestEntry.request_id) === String(selectedRequestId)) || requestQueueRows[0] || null,
    [requestQueueRows, selectedRequestId],
  );
  const activeHistoryRow = useMemo(
    () => filingHistoryRows.find((entry) => String(entry.id) === String(selectedHistoryId)) || filingHistoryRows[0] || null,
    [filingHistoryRows, selectedHistoryId],
  );
  const activeRequestDraft = activeRequestRow
    ? (requestDraftsById[String(activeRequestRow.requestEntry.request_id)] || buildRequestComposerSeed(activeRequestRow.requestEntry))
    : buildRequestComposerSeed(null);
  const packDominantBlocker = packOps.dominant_blocker || primaryBlocker || null;
  const packNextAction = packOps.next_recommended_action || filingData.submission_pack_state?.next_recommended_action || null;
  const requestCompareItems = activeRequestRow
    ? [
        {
          label: "Status vs letzte Aktivitaet",
          primary: activeRequestRow.status,
          secondary: `Letzte Aktivitaet ${activeRequestRow.lastActivity}`,
        },
        {
          label: "Pack-Relevanz vs naechster Schritt",
          primary: activeRequestRow.requestEntry.pack_relevance_label || activeRequestRow.requestEntry.pack_relevance || "Wirkt direkt auf Pack-Reife",
          secondary: activeRequestRow.nextStep,
        },
        {
          label: "Antwortstand vs Review",
          primary: activeRequestRow.requestEntry.response_status ? formatResponseStatus(activeRequestRow.requestEntry.response_status) : "Noch keine Rueckmeldung",
          secondary: activeRequestRow.requestEntry.latest_response_summary?.preview_text || "Noch kein Rueckmeldungsauszug hinterlegt.",
        },
      ]
    : [];
  const historyCompareItems = activeHistoryRow
    ? [
        {
          label: "Snapshot vs aktueller Workspace",
          primary: `Version ${activeHistoryRow.entry?.version_no || "k.A."} · ${activeHistoryRow.stage}`,
          secondary: `Heute ${filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · Freeze ${currentPack?.freeze_state || "offen"}`,
        },
        {
          label: "Delta vs naechster Blick",
          primary: activeHistoryRow.entry?.previous_version_delta?.summary_lines?.[0] || "Kein kompaktes Delta gegen die Vorversion hinterlegt.",
          secondary: packNextAction?.label || "Weiter im aktuellen Gate-Kontext lesen.",
        },
        {
          label: "Gate-Kontext",
          primary: activeHistoryRow.entry?.history_summary?.current_pack_is_stale ? "Snapshot war veraltet" : "Snapshot war belastbar",
          secondary: `${activeHistoryRow.entry?.history_summary?.blocking_count || 0} Blocker · ${activeHistoryRow.entry?.history_summary?.warning_count || 0} Warnungen`,
        },
      ]
    : [];
  const packSecondaryActions = [
    {
      key: "reopen",
      label: "Zur Bearbeitung zurueck",
      enabled: Boolean(packOps.gates?.can_reopen_pack && currentPack),
      reason: !currentPack ? "Kein Freeze-Stand vorhanden." : "Nur nach einem bestehenden Freeze-Stand wieder zur Bearbeitung oeffnen.",
      onClick: () => onRunPackAction(row.id, "reopen_pack", currentPack?.final_pack_id),
    },
    {
      key: "signature",
      label: "Als signaturbereit markieren",
      enabled: Boolean(packOps.gates?.can_mark_ready_for_signature && currentPack),
      reason: "Erst aktiv, wenn der aktuelle Freeze-Stand die Signaturfreigabe traegt.",
      onClick: () => onRunPackAction(row.id, "mark_ready_for_signature", currentPack?.final_pack_id),
    },
    {
      key: "submission",
      label: "Als einreichungsbereit markieren",
      enabled: Boolean(packOps.gates?.can_mark_ready_for_submission && currentPack),
      reason: "Erst aktiv, wenn Signatur- und Gate-Stand fuer die Einreichung freigegeben sind.",
      onClick: () => onRunPackAction(row.id, "mark_ready_for_submission", currentPack?.final_pack_id),
    },
  ];
  const packRiskAction = {
    label: "Archivieren",
    enabled: Boolean(packOps.gates?.can_archive_pack && currentPack),
    reason: "Nur archivieren, wenn der Pack-Stand fachlich abgeschlossen ist und nicht weiter bearbeitet werden soll.",
    onClick: () => onRunPackAction(row.id, "archive_pack", currentPack?.final_pack_id),
  };
  const latestHistory = filingData.history?.[0] || null;
  const trainingTask = trainingGuidance
    ? (
      openTasks.find((task) => String(task.task_id) === String(trainingGuidance.highlightedTaskId || ""))
      || openTasks.find((task) => task.requires_candidate_input || task.owner_role === "candidate")
      || null
    )
    : null;
  const trainingRequest = trainingGuidance
    ? (
      allRequests.find((entry) => String(entry.request_id) === String(trainingGuidance.requestId || requestedRequestId || ""))
      || openCandidateRequest
      || null
    )
    : null;
  const trainingPrimaryAction = (() => {
    if (!trainingGuidance) return null;
    if (trainingRequest?.status === "draft") {
      return {
        label: "Nachforderung jetzt senden",
        onClick: () => onUpdateCandidateRequest(row.id, trainingRequest, "sent"),
      };
    }
    if (trainingTask && (trainingTask.requires_candidate_input || trainingTask.owner_role === "candidate")) {
      return {
        label: "Nachforderung jetzt anlegen",
        onClick: () => onCreateCandidateRequest(row.id, trainingTask, true),
      };
    }
    if (trainingRequest?.status === "answered") {
      return {
        label: "Rueckmeldung jetzt pruefen",
        onClick: () => onUpdateCandidateRequest(row.id, trainingRequest, "answered", { response_status: "response_under_review" }),
      };
    }
    if (trainingRequest && ["sent", "viewed"].includes(trainingRequest.status)) {
      return {
        label: "Rueckmeldung als eingegangen markieren",
        onClick: () => onUpdateCandidateRequest(row.id, trainingRequest, "answered", { response_status: "response_received" }),
      };
    }
    return null;
  })();
  const employerPrimaryAction = (() => {
    if (!openEmployerTask) return null;
    const candidateDependent = isCandidateFollowupTask(openEmployerTask);
    if (candidateDependent) {
      if (!openEmployerTaskRequest) {
        return {
          label: "Nachforderung jetzt senden",
          onClick: () => onCreateCandidateRequest(row.id, openEmployerTask, true),
        };
      }
      if (openEmployerTaskRequest.status === "draft") {
        return {
          label: "Nachforderung jetzt senden",
          onClick: () => onUpdateCandidateRequest(row.id, openEmployerTaskRequest, "sent"),
        };
      }
      if (canSendCandidateReminder(openEmployerTaskRequest)) {
        return {
          label: getCandidateReminderLabel(openEmployerTaskRequest),
          onClick: () => onUpdateCandidateRequest(row.id, openEmployerTaskRequest, openEmployerTaskRequest.status, { reminder: true }),
        };
      }
      if (openEmployerTaskRequest.awaiting_employer_review || ["response_received", "response_under_review", "response_incomplete", "response_rejected"].includes(openEmployerTaskRequest.response_status)) {
        return {
          label: "Rueckmeldung pruefen",
          onClick: () => onTabChange("candidate"),
        };
      }
    }
    return {
      label: "In Arbeit",
      onClick: () => onUpdateAuthorityTask(row.id, openEmployerTask, "progress"),
    };
  })();

  useEffect(() => {
    if (requestedRequestId && requestQueueRows.some((entry) => String(entry.requestEntry.request_id) === String(requestedRequestId))) {
      setSelectedRequestId(String(requestedRequestId));
      setRequestDrawerVisible(true);
      return;
    }
    if (!requestQueueRows.length) {
      setSelectedRequestId("");
      return;
    }
    if (!requestQueueRows.some((entry) => String(entry.requestEntry.request_id) === String(selectedRequestId))) {
      setSelectedRequestId(String(requestQueueRows[0].requestEntry.request_id));
    }
  }, [requestQueueRows, requestedRequestId, selectedRequestId]);

  useEffect(() => {
    if (!requestQueueRows.length) {
      setRequestDraftsById({});
      return;
    }
    setRequestDraftsById((current) => {
      let changed = false;
      const next = { ...current };
      requestQueueRows.forEach((entry) => {
        const requestId = String(entry.requestEntry.request_id);
        if (!next[requestId]) {
          next[requestId] = buildRequestComposerSeed(entry.requestEntry);
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [requestQueueRows]);

  useEffect(() => {
    if (!filingHistoryRows.length) {
      setSelectedHistoryId("");
      return;
    }
    if (!filingHistoryRows.some((entry) => String(entry.id) === String(selectedHistoryId))) {
      setSelectedHistoryId(String(filingHistoryRows[0].id));
    }
  }, [filingHistoryRows, selectedHistoryId]);
  const openRequestDetails = (requestEntry) => {
    if (!requestEntry?.request_id) return;
    setSelectedRequestId(String(requestEntry.request_id));
    setRequestDrawerVisible(true);
  };
  const patchRequestDraft = (requestId, patch) => {
    setRequestDraftsById((current) => ({
      ...current,
      [String(requestId)]: {
        ...(current[String(requestId)] || buildRequestComposerSeed(activeRequestRow?.requestEntry || null)),
        ...(patch || {}),
      },
    }));
  };
  const saveCandidateRequestDraft = async (requestEntry, nextStatus = null, extra = {}) => {
    if (!requestEntry?.request_id) return;
    const draft = requestDraftsById[String(requestEntry.request_id)] || buildRequestComposerSeed(requestEntry);
    await onUpdateCandidateRequest(
      row.id,
      requestEntry,
      nextStatus || requestEntry.status,
      {
        ...extra,
        message_subject: String(draft.subject || "").trim(),
        message_html: normalizeRequestMessageHtml(draft.html),
      },
    );
  };
  const resolveTaskPrimaryAction = (task) => {
    if (!task) return null;
    if (!isCandidateFollowupTask(task)) {
      return {
        label: "In Arbeit",
        onClick: () => onUpdateAuthorityTask(row.id, task, "progress"),
      };
    }
    const requestEntry = resolveTaskRequest(task, activeRequests);
    if (!requestEntry) {
      return {
        label: "Nachforderung jetzt senden",
        onClick: () => onCreateCandidateRequest(row.id, task, true),
      };
    }
    if (requestEntry.status === "draft") {
      return {
        label: "Nachforderung jetzt senden",
        onClick: () => onUpdateCandidateRequest(row.id, requestEntry, "sent"),
      };
    }
    if (canSendCandidateReminder(requestEntry)) {
      return {
        label: getCandidateReminderLabel(requestEntry),
        onClick: () => onUpdateCandidateRequest(row.id, requestEntry, requestEntry.status, { reminder: true }),
      };
    }
    if (requestEntry.awaiting_employer_review || ["response_received", "response_under_review", "response_incomplete", "response_rejected"].includes(requestEntry.response_status)) {
      return {
        label: "Rueckmeldung pruefen",
        onClick: () => onTabChange("candidate"),
      };
    }
    return {
      label: "In Arbeit",
      onClick: () => onUpdateAuthorityTask(row.id, task, "progress"),
    };
  };
  const workspacePrimaryAction = trainingPrimaryAction
    || employerPrimaryAction
    || ((activeTab === "pack" && (packOps.gates?.can_freeze_for_review || packOps.gates?.can_refreeze))
      ? {
          label: packOps.gates?.can_refreeze ? "Pack neu einfrieren" : "Pack fuer Pruefung einfrieren",
          onClick: () => onFinalizeSubmissionPack(row.id),
        }
      : null);
  const packActionHierarchyHint = workspacePrimaryAction
    ? `Primaer bleibt jetzt ${workspacePrimaryAction.label}. Erst danach folgen Ruecknahme-, Freigabe- oder Archiv-Schritte.`
    : (packDominantBlocker?.detail || packDominantBlocker?.title || "Aktuell gibt es keine dominante Pack-Aktion. Erst Blocker oder offene Requests sauber klaeren.");
  const packReadbackTone = detailState.finalizingPack ? "info" : (detailState.filingFlash ? "success" : (packDominantBlocker ? "warning" : "info"));
  const packReadbackSummary = detailState.finalizingPack
    ? `Zustandswechsel laeuft: ${workspacePrimaryAction?.label || "Pack-Aktion"} wird verarbeitet.`
    : detailState.filingFlash
      ? `${detailState.filingFlash} Neuer Stand: ${filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"}.`
      : `Aktueller Pack-Stand: ${filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · Freeze ${currentPack?.freeze_state || "noch keiner"}.`;
  const workspaceStatusItems = [
    {
      label: "Fallstatus",
      value: filingData.review_workflow?.overall_label || "wird berechnet",
      note: primaryBlocker?.title || "Aktuell kein dominanter Blocker sichtbar.",
      tone: filingData.completion_workflow?.blocking_count ? "warning" : "success",
    },
    {
      label: "Naechste Arbeitgeberaktion",
      value: openEmployerTask?.title || "Kein direkter Arbeitgebertask",
      note: openEmployerTask?.description || openEmployerTask?.action_label || "Der Fall ist aus Arbeitgebersicht aktuell auf Stand.",
    },
    {
      label: "Naechste Bewerberaktion",
      value: openCandidateRequest?.title || "Kein offener Kandidatenschritt",
      note: formatRequestNextStep(openCandidateRequest),
    },
    {
      label: "Pack-Stand",
      value: currentPack ? `${currentPack.stage_label || currentPack.stage} / ${currentPack.freeze_state || "eingefroren"}` : "Noch kein Freeze",
      note: `${preparedDocumentCount} Unterlagen vorbereitet · ${openPointCount} offene Punkte`,
      tone: currentPack?.is_stale ? "warning" : (currentPack ? "success" : undefined),
    },
  ];

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const updateLayout = (event) => {
      setWorkspaceStepperLayout(event.matches ? "horizontal" : "vertical");
    };
    updateLayout(mediaQuery);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateLayout);
      return () => mediaQuery.removeEventListener("change", updateLayout);
    }
    mediaQuery.addListener(updateLayout);
    return () => mediaQuery.removeListener(updateLayout);
  }, []);

  useEffect(() => {
    setRequestQueuePage((current) => Math.min(current, requestQueuePageCount));
  }, [requestQueuePageCount]);

  useEffect(() => {
    setHistoryQueuePage((current) => Math.min(current, historyQueuePageCount));
  }, [historyQueuePageCount]);

  return (
    <div className="employer-filing-workspace">
      <EmployerStatusBand
        kicker="Transactional Evidence Surface"
        title={filingData.employer_ready_pack?.cover_title || filingData.filing_packet_summary?.headline}
        note={`${filingData.classification?.procedure_explainer?.label || filingData.classification?.pathway_title || "Verfahrenspfad"} · ${filingData.classification?.federal_state || "Bundesland offen"}${filingData.classification?.city ? ` / ${filingData.classification.city}` : ""}`}
        items={workspaceStatusItems}
        actions={(
          <>
            {workspacePrimaryAction ? (
              <CLoadingButton
                className="dashboard-action-button"
                disabledOnLoading
                loading={detailState.filingLoading || detailState.finalizingPack}
                onClick={workspacePrimaryAction.onClick}
              >
                {workspacePrimaryAction.label}
              </CLoadingButton>
            ) : null}
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onExportKit(row.id, "json")} disabled={detailState.exporting}>
              JSON Export
            </Button>
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onExportKit(row.id, "html")} disabled={detailState.exporting}>
              HTML Export
            </Button>
          </>
        )}
      />

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Was jetzt zu tun ist</p>
            <p className="mt-2 text-lg font-semibold text-slate-900">Die Seite fuehrt jetzt ueber wenige klare Schritte statt ueber alle Daten gleichzeitig.</p>
            <p className="mt-1 text-sm text-slate-600">
              Signierbar {filingData.task_board?.signable_count || 0} · uploadbar {filingData.task_board?.uploadable_count || 0} · blocker {filingData.completion_workflow?.blocking_count || 0}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("candidate")}>
              Bewerberanforderungen
            </Button>
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("documents")}>
              Dokumente
            </Button>
            <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("pack")}>
              Einreichungspaket
            </Button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 xl:grid-cols-3">
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-sky-700">Arbeitgeber</p>
            <p className="mt-2 font-semibold text-slate-900">{openEmployerTask?.title || "Kein direkter Arbeitgeber-Task offen"}</p>
            <p className="mt-1 text-sm text-slate-700">{openEmployerTask?.action_label || openEmployerTask?.description || "Der Fall braucht aktuell keine unmittelbare Arbeitgeberaktion."}</p>
            {openEmployerTask ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" className="dashboard-inline-button" onClick={employerPrimaryAction?.onClick}>
                  {employerPrimaryAction?.label || "In Arbeit"}
                </Button>
                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, openEmployerTask, "complete")}>Als erledigt markieren</Button>
              </div>
            ) : null}
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-emerald-700">Bewerber</p>
            <p className="mt-2 font-semibold text-slate-900">{openCandidateRequest?.title || "Keine offene Bewerberanforderung"}</p>
            <p className="mt-1 text-sm text-slate-700">{formatRequestNextStep(openCandidateRequest)}</p>
            {openCandidateRequest ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`rounded-full border px-2 py-1 text-[11px] ${getDeadlineTone(openCandidateRequest)}`}>
                  {formatRequestReadableState(openCandidateRequest, formatRequestStatus)}
                </span>
                <span className="rounded-full border border-white bg-white px-2 py-1 text-[11px] text-slate-600">
                  Faellig {formatDateTime(openCandidateRequest.due_at)}
                </span>
                {canSendCandidateReminder(openCandidateRequest) ? (
                  <Button
                    variant="outline"
                    className="dashboard-inline-button"
                    onClick={() => onUpdateCandidateRequest(row.id, openCandidateRequest, openCandidateRequest.status, { reminder: true })}
                  >
                    {getCandidateReminderLabel(openCandidateRequest)}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-amber-700">Blocker</p>
            <p className="mt-2 font-semibold text-slate-900">{primaryBlocker?.title || "Kein aktiver Blocker"}</p>
            <p className="mt-1 text-sm text-slate-700">
              {primaryBlocker?.action_label || primaryBlocker?.request_reason || primaryBlocker?.description || "Der Fall ist aktuell nicht durch einen grossen Einzelpunkt blockiert."}
            </p>
          </div>
        </div>
        {(filingData.completion_workflow?.summary_lines?.length || filingData.task_board?.summary_lines?.length) ? (
          <CCallout className="mt-4 mb-0" color="info">
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
              {[...(filingData.completion_workflow?.summary_lines || []), ...(filingData.task_board?.summary_lines || [])].slice(0, 5).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </CCallout>
        ) : null}
        {trainingGuidance ? (
          <CCallout className="mt-4 mb-0" color="warning">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.18em] text-amber-800">Naechster Trainingsschritt im Filing</p>
                <p className="mt-2 text-base font-semibold text-slate-900">{trainingGuidance.title}</p>
                <p className="mt-1 text-sm text-amber-950">{trainingGuidance.detail}</p>
                <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-amber-950">
                  <li>Oeffne den markierten Abschnitt in dieser Filing-Flaeche.</li>
                  <li>Arbeite den hervorgehobenen Schritt zuerst ab.</li>
                  <li>Nutze danach erst weitere Dokumente oder Pack-Aktionen.</li>
                </ol>
                <p className="mt-3 text-xs text-amber-800">
                  Stage {trainingGuidance.stageLabel}
                  {trainingTask?.title ? ` · Zieltask ${trainingTask.title}` : ""}
                  {trainingRequest?.title ? ` · Zielrequest ${trainingRequest.title}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange(trainingGuidance.targetTab || "candidate")}>
                  {trainingGuidance.buttonLabel || "Zum relevanten Abschnitt"}
                </Button>
                {trainingPrimaryAction ? (
                  <CLoadingButton className="dashboard-action-button" disabledOnLoading loading={detailState.filingLoading} onClick={trainingPrimaryAction.onClick}>
                    {trainingPrimaryAction.label}
                  </CLoadingButton>
                ) : null}
              </div>
            </div>
          </CCallout>
        ) : null}
        <FilingDisclosure
          title="Alle offenen Schritte im Detail"
          subtitle="Hier bleiben die persistente Task-Logik und alle Aktionen erhalten, aber nicht mehr als Primaerflaeche."
        >
          <div className="space-y-3">
            {openTasks.length ? openTasks.slice(0, 10).map((task) => (
              (() => {
                const taskPrimaryAction = resolveTaskPrimaryAction(task);
                return (
              <div
                key={`task-${task.task_id}`}
                className={`rounded-lg border bg-white p-3 ${
                  trainingGuidance && String(trainingTask?.task_id || "") === String(task.task_id)
                    ? "border-amber-300 ring-2 ring-amber-100"
                    : "border-slate-200"
                }`}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-slate-900">{task.title}</p>
                      <span className={`rounded-full border px-2 py-1 text-[11px] ${getResponsibilityMeta(task.owner_role).tone}`}>{formatOwnerRoleLabel(task.owner_role)}</span>
                      <span className={`rounded-full border px-2 py-1 text-[11px] ${getReviewBucketMeta(task.source_bucket || "manual_only").tone}`}>{formatReviewBucketLabel(task.source_bucket || task.source_type)}</span>
                      {trainingGuidance && String(trainingTask?.task_id || "") === String(task.task_id) ? (
                        <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-900">Trainingsschritt</span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{task.description || task.action_label || "Offener Schritt im Filing-Fall."}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Status {formatTaskStatus(task.status)} · Prioritaet {task.priority || "mittel"}
                      {task.requires_signature ? " · Unterschrift noetig" : ""}
                      {task.requires_upload ? " · Upload noetig" : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, task, "complete")}>Erledigt</Button>
                    <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateAuthorityTask(row.id, task, task.status === "blocked" ? "reopen" : "block")}>
                      {task.status === "blocked" ? "Entblocken" : "Blockieren"}
                    </Button>
                    {taskPrimaryAction ? (
                      <Button variant="outline" className="dashboard-inline-button" onClick={taskPrimaryAction.onClick}>
                        {taskPrimaryAction.label}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-2 lg:flex-row">
                  <textarea
                    className="min-h-[74px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
                    placeholder="Kurze Arbeitsnotiz zu diesem Schritt"
                    value={detailState.taskNotes?.[String(task.task_id)] ?? task.completion_note ?? ""}
                    onChange={(event) => onTaskNoteChange(row.id, task.task_id, event.target.value)}
                  />
                  <Button variant="outline" className="dashboard-inline-button self-start" onClick={() => onUpdateAuthorityTask(row.id, task, "note")}>
                    Notiz speichern
                  </Button>
                </div>
              </div>
                );
              })()
            )) : (
              <p className="text-sm text-slate-600">Keine offenen Filing-Schritte mehr.</p>
            )}
          </div>
        </FilingDisclosure>
      </div>

      <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-4">
        <CCallout color="info" className="mb-0">
          <strong>Gefuehrter Filing-Pfad</strong>
          <p className="mb-0 mt-2">
            Der Workspace fuehrt dieselben vorhandenen Stufen wie die Filing-Logik selbst: Kandidatenanforderungen, Dokumente, Pack-Reife und Verlauf.
          </p>
        </CCallout>
        <CStepper
          activeStepNumber={activeWorkspaceStepNumber}
          layout={workspaceStepperLayout}
          onStepChange={(stepNumber) => {
            const nextStep = workspaceStepperSteps[Math.max(0, Number(stepNumber) - 1)];
            if (nextStep?.key) onTabChange(nextStep.key);
          }}
          steps={workspaceStepperSteps.map((step) => ({ label: step.label }))}
        />
        <TabsList className="h-auto w-full flex-wrap justify-start gap-2 rounded-2xl bg-slate-100 p-2">
          <TabsTrigger value="candidate" className="rounded-xl">Kandidatenanforderungen</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-xl">Dokumente & Formulare</TabsTrigger>
          <TabsTrigger value="pack" className="rounded-xl">Finales Einreichungspaket</TabsTrigger>
          <TabsTrigger value="history" className="rounded-xl">Verlauf & Historie</TabsTrigger>
        </TabsList>

        <TabsContent value="candidate" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Kandidatenanforderungen</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">Offene Anforderungen an den Bewerber sind jetzt separat und leicht lesbar gebuendelt.</p>
                <p className="mt-1 text-sm text-slate-600">Was fehlt, warum es gebraucht wird, bis wann es benoetigt wird und was als Naechstes passiert.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{filteredRequestGroups.open.length} offen</span>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">{filteredRequestGroups.review.length} in Pruefung</span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">{filteredRequestGroups.done.length} erledigt</span>
              </div>
            </div>
            <div className="mt-4 grid gap-3 xl:grid-cols-[1fr,1fr]">
              <div>
                <CFormLabel className="mb-2 text-xs uppercase tracking-[0.18em] text-slate-400">Request-Status filtern</CFormLabel>
                <CMultiSelect
                  cleaner
                  multiple
                  options={requestStatusOptions}
                  optionsStyle="checkbox"
                  placeholder="Status auswaehlen"
                  search
                  selectionType="tags"
                  value={requestStatusFilter}
                  onChange={(selected) => setRequestStatusFilter(selected.map((item) => String(item.value)))}
                />
              </div>
              <div>
                <CFormLabel className="mb-2 text-xs uppercase tracking-[0.18em] text-slate-400">Deadline-Fenster</CFormLabel>
                <CDateRangePicker
                  cleaner
                  endDate={requestDeadlineRange.endDate}
                  locale="de-DE"
                  onEndDateChange={(date) => setRequestDeadlineRange((current) => ({ ...current, endDate: date }))}
                  onStartDateChange={(date) => setRequestDeadlineRange((current) => ({ ...current, startDate: date }))}
                  placeholder={["Ab Datum", "Bis Datum"]}
                  startDate={requestDeadlineRange.startDate}
                />
              </div>
            </div>
            <CCallout color="info" className="mt-4 mb-0">
              <strong>Operative Leselogik</strong>
              <div>Kandidatenanforderungen bleiben der Ort fuer Fristen, Nachforderungen und Review. Der Pack-Bereich ist dafuer bewusst getrennt.</div>
            </CCallout>
            {filingData.delivery_board ? (
              <CCallout color="info" className="mt-4 mb-0">
                Letzte Zustellung: Mails gesendet {filingData.delivery_board.email_sent_count || 0} · offen/pending {filingData.delivery_board.email_pending_count || 0} · Fehler {filingData.delivery_board.delivery_failure_count || 0} · Retry {filingData.delivery_board.retry_count || 0}
              </CCallout>
            ) : null}
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Request Queue</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">Die operative Queue zeigt offene, pruefende und erledigte Kandidatenanforderungen in einer belastbaren Review-Liste.</p>
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{requestQueueRows.length} Requests im Filter</span>
              </div>
              {requestQueueRows.length ? (
                <>
                  <div className="mt-4">
                    <CSmartTable
                      columns={[
                        { key: "bucket", label: "Lane" },
                        { key: "title", label: "Anforderung" },
                        { key: "status", label: "Status" },
                        { key: "dueAt", label: "Frist" },
                        { key: "lastActivity", label: "Letzte Aktivitaet" },
                        { key: "urgency", label: "Dringlichkeit" },
                        { key: "nextStep", label: "Naechster Schritt" },
                        { key: "actions", label: "Aktion", sorter: false, filter: false },
                      ]}
                      items={pagedRequestQueueRows}
                      pagination={false}
                      sorter={false}
                      tableProps={{ hover: true, responsive: true }}
                      scopedColumns={{
                        status: (item) => (
                          <td>
                            <span className={`rounded-full border px-2 py-1 text-[11px] ${item.tone}`}>{item.status}</span>
                          </td>
                        ),
                        actions: (item) => (
                          <td>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant={String(activeRequestRow?.requestEntry?.request_id || "") === String(item.requestEntry.request_id) ? "default" : "outline"}
                                className="dashboard-inline-button"
                                onClick={() => openRequestDetails(item.requestEntry)}
                              >
                                {String(activeRequestRow?.requestEntry?.request_id || "") === String(item.requestEntry.request_id) && requestDrawerVisible ? "Details offen" : "Details"}
                              </Button>
                              {item.requestEntry.status === "draft" ? (
                                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, item.requestEntry, "sent")}>Senden</Button>
                              ) : null}
                              {canSendCandidateReminder(item.requestEntry) ? (
                                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, item.requestEntry, item.requestEntry.status, { reminder: true })}>{getCandidateReminderLabel(item.requestEntry)}</Button>
                              ) : null}
                              {item.requestEntry.status === "answered" ? (
                                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, item.requestEntry, "answered", { response_status: "response_under_review" })}>Pruefen</Button>
                              ) : null}
                              {!["fulfilled", "obsolete", "expired"].includes(item.requestEntry.status) ? (
                                <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, item.requestEntry, "fulfilled", { response_status: "response_accepted" })}>Akzeptieren</Button>
                              ) : null}
                            </div>
                          </td>
                        ),
                      }}
                    />
                  </div>
                  {requestQueueRows.length > requestQueuePageSize ? (
                    <div className="mt-3 flex justify-end">
                      <CSmartPagination
                        activePage={requestQueuePage}
                        align="end"
                        dots
                        doubleArrows
                        onActivePageChange={setRequestQueuePage}
                        pages={requestQueuePageCount}
                      />
                    </div>
                  ) : null}
                  {activeRequestRow ? (
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Request Detail</p>
                          <p className="mt-2 text-lg font-semibold text-slate-900">{activeRequestRow.requestEntry.title}</p>
                          <p className="mt-1 text-sm text-slate-600">
                            Status {activeRequestRow.status} · Frist {activeRequestRow.dueAt} · letzte Aktivitaet {activeRequestRow.lastActivity}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-3 py-1 text-xs ${activeRequestRow.tone}`}>{activeRequestRow.bucket}</span>
                          <Button variant="outline" className="dashboard-inline-button" onClick={() => openRequestDetails(activeRequestRow.requestEntry)}>
                            Details & Schreiben
                          </Button>
                        </div>
                      </div>
                      <CCallout color={activeRequestRow.requestEntry.overdue ? "danger" : (activeRequestRow.requestEntry.awaiting_employer_review ? "warning" : "info")} className="mt-4 mb-0">
                        <strong>Request Handoff</strong>
                        <div>
                          Stand jetzt: {activeRequestRow.status}. Blockiert wird der naechste Schritt durch {activeRequestRow.requestEntry.overdue ? "die Fristlage" : (activeRequestRow.requestEntry.awaiting_employer_review ? "den offenen Employer-Review" : "den noch offenen Ruecklauf")}.
                        </div>
                        <div className="mt-2">
                          Filing-Relevanz: {activeRequestRow.requestEntry.pack_relevance_label || activeRequestRow.requestEntry.pack_relevance || "wirkt direkt auf Pack-Reife und Review."} · naechster zulaessiger Schritt: {activeRequestRow.nextStep}
                        </div>
                      </CCallout>
                      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {requestCompareItems.map((item) => (
                          <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-3">
                            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                            <p className="mt-2 font-semibold text-slate-900">{item.primary}</p>
                            <p className="mt-1 text-sm text-slate-600">{item.secondary}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Pruefstand</p>
                          <p className="mt-2 text-sm text-slate-900">{activeRequestRow.requestEntry.response_status ? formatResponseStatus(activeRequestRow.requestEntry.response_status) : "Noch keine Rueckmeldung"}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Reminder</p>
                          <p className="mt-2 text-sm text-slate-900">{activeRequestRow.requestEntry.reminder_count || 0} gesendet</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Antwortkanal</p>
                          <p className="mt-2 text-sm text-slate-900">{formatDateTime(activeRequestRow.requestEntry.delivery_summary?.last_email_delivery_at) || "Noch kein Versand"}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechste zulaessige Aktion</p>
                          <p className="mt-2 text-sm text-slate-900">{activeRequestRow.nextStep}</p>
                        </div>
                      </div>
                      {activeRequestRow.requestEntry.latest_response_summary?.preview_text ? (
                        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                          <strong className="text-slate-900">Letzte Rueckmeldung:</strong> {activeRequestRow.requestEntry.latest_response_summary.preview_text}
                        </div>
                      ) : null}
                      <CCallout color={activeRequestRow.requestEntry.awaiting_employer_review ? "warning" : "info"} className="mt-4 mb-0">
                        <strong>Evidence Continuity</strong>
                        <div>
                          Aktueller Stand: {activeRequestRow.status}. Daraus ergibt sich als naechster sinnvoller Blick {activeRequestRow.nextStep}
                        </div>
                        <div className="mt-2">
                          {activeRequestRow.requestEntry.latest_response_summary?.preview_text
                            ? "Die letzte Rueckmeldung bleibt direkt an den Review-Kontext gekoppelt."
                            : "Ohne Rueckmeldung bleibt der Fokus auf Frist, Versand und Pack-Relevanz."}
                        </div>
                      </CCallout>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="mt-4 text-sm text-slate-500">Keine Requests im aktuellen Filter.</p>
              )}
            </div>
            <div className="mt-4 grid gap-4 xl:grid-cols-3">
              {[
                { key: "open", title: "Offene Anforderungen", description: "Hier fehlt noch etwas vom Bewerber." },
                { key: "review", title: "In Pruefung", description: "Rueckmeldungen sind eingegangen und muessen bewertet werden." },
                { key: "done", title: "Erledigt", description: "Diese Anforderungen sind abgeschlossen oder nicht mehr relevant." },
              ].map((group) => (
                <div key={group.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">{group.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{group.description}</p>
                  <div className="mt-3 space-y-3">
                    {(filteredRequestGroups[group.key] || []).length ? (filteredRequestGroups[group.key] || []).slice(0, 6).map((requestEntry) => (
                      <div
                        key={`request-${requestEntry.request_id}`}
                        className={`rounded-lg border bg-white p-3 ${
                          trainingGuidance && String(trainingRequest?.request_id || "") === String(requestEntry.request_id)
                            ? "border-amber-300 ring-2 ring-amber-100"
                            : String(requestEntry.request_id) === String(requestedRequestId)
                              ? "border-teal-300 ring-2 ring-teal-100"
                              : "border-slate-200"
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{requestEntry.title}</p>
                          <span className={`rounded-full border px-2 py-1 text-[11px] ${getDeadlineTone(requestEntry)}`}>{formatRequestReadableState(requestEntry, formatRequestStatus)}</span>
                          {trainingGuidance && String(trainingRequest?.request_id || "") === String(requestEntry.request_id) ? (
                            <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-900">Hier jetzt weiter</span>
                          ) : null}
                        </div>
                        <p className="mt-2 text-sm text-slate-600">{requestEntry.request_reason || requestEntry.description || "Filing-Anforderung"}</p>
                        <p className="mt-2 text-xs text-slate-500">
                          Frist {formatDateTime(requestEntry.due_at)} · Letzte Aktivitaet {formatDateTime(requestEntry.last_activity_at)} · Naechster Schritt {formatRequestNextStep(requestEntry)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Dringlichkeit {formatUrgencyLabel(requestEntry.urgency)} · Reminder {requestEntry.reminder_count || 0}x · Letzte Mail {formatDateTime(requestEntry.delivery_summary?.last_email_delivery_at)}
                        </p>
                        {requestEntry.response_status ? (
                          <p className="mt-1 text-xs text-slate-500">Pruefstand: {formatResponseStatus(requestEntry.response_status)}</p>
                        ) : null}
                        {requestEntry.latest_response_summary?.preview_text ? (
                          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                            <strong className="text-slate-900">Letzte Rueckmeldung:</strong> {requestEntry.latest_response_summary.preview_text}
                          </div>
                        ) : null}
                        {group.key !== "done" ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {requestEntry.status === "draft" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "sent")}>Senden</Button>
                            ) : null}
                            {canSendCandidateReminder(requestEntry) ? (
                              <Button
                                variant="outline"
                                className="dashboard-inline-button"
                                onClick={() => onUpdateCandidateRequest(row.id, requestEntry, requestEntry.status, { reminder: true })}
                              >
                                {getCandidateReminderLabel(requestEntry)}
                              </Button>
                            ) : null}
                            {["sent", "viewed"].includes(requestEntry.status) ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_received" })}>Antwort erhalten</Button>
                            ) : null}
                            {requestEntry.status === "answered" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_under_review" })}>In Pruefung</Button>
                            ) : null}
                            {["answered", "viewed", "sent"].includes(requestEntry.status) ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "fulfilled", { response_status: "response_accepted" })}>Akzeptieren</Button>
                            ) : null}
                            {requestEntry.status === "answered" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_incomplete" })}>Unvollstaendig</Button>
                            ) : null}
                            {requestEntry.status === "answered" ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "answered", { response_status: "response_rejected" })}>Nacharbeit</Button>
                            ) : null}
                            {!["fulfilled", "obsolete", "expired"].includes(requestEntry.status) ? (
                              <Button variant="outline" className="dashboard-inline-button" onClick={() => onUpdateCandidateRequest(row.id, requestEntry, "obsolete")}>Nicht mehr relevant</Button>
                            ) : null}
                          </div>
                        ) : null}
                        {group.key === "done" ? (
                          <p className="mt-3 text-xs text-slate-500">
                            Fuer erledigte Anforderungen ist kein Reminder mehr noetig. Wenn du erneut nachfassen willst, fuehre das Thema zuerst wieder als offene Nachforderung.
                          </p>
                        ) : null}
                      </div>
                    )) : (
                      <p className="text-sm text-slate-500">In diesem Bereich liegt aktuell nichts.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Dokumente & Formulare</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">Die Dokumente bleiben vollstaendig erhalten, aber zeigen standardmaessig nur noch das, was fuer die Arbeit jetzt relevant ist.</p>
                <p className="mt-1 text-sm text-slate-600">Titel, kurzer Sinn, Status, Verantwortlichkeit und naechste Aktion vorne. Technische Tiefe erst auf Wunsch.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">{filingData.fillability_summary?.fillable_pdf || 0} direkt vorbereitbar</span>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">{filingData.fillability_summary?.structured_transfer_only || 0} Transferhilfen</span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{missingDocuments.length} fehlen/manuell</span>
              </div>
            </div>
            <div className="mt-4">
              <CFormLabel className="mb-2 text-xs uppercase tracking-[0.18em] text-slate-400">Dokumentenstatus filtern</CFormLabel>
              <CMultiSelect
                cleaner
                multiple
                options={documentBucketOptions}
                optionsStyle="checkbox"
                placeholder="Review-Buckets auswaehlen"
                search
                selectionType="tags"
                value={documentBucketFilter}
                onChange={(selected) => setDocumentBucketFilter(selected.map((item) => String(item.value)))}
              />
            </div>
            <CCallout color="info" className="mt-4 mb-0">
              <strong>Dokumentenfokus</strong>
              <div>Die Filter schraenken nur die Sicht ein. Die Filing-Logik, Pflichtdokumente und Verantwortlichkeiten bleiben unveraendert.</div>
            </CCallout>
            <div className="mt-4 space-y-3">
              {filteredOperativeDocuments.map((entry) => {
                const missingEntry = missingById.get(String(entry.id));
                return (
                  <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{entry.title}</p>
                          <span className={`rounded-full border px-2 py-1 text-[11px] ${getReviewBucketMeta(entry.review_bucket).tone}`}>{formatReviewBucketLabel(entry.review_bucket)}</span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">{entry.review_note || entry.fillability_reason || entry.authority || "Offizielle Unterlage im Filing-Paket."}</p>
                        <p className="mt-2 text-xs text-slate-500">
                          Verantwortlich: {formatDocumentResponsibility(entry, missingEntry)} · Jetzt zu tun: {formatDocumentAction(entry, missingEntry)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {entry.download_url ? (
                          <Button variant="outline" className="dashboard-inline-button" onClick={() => onDownloadAuthorityDocument(row.id, entry)} disabled={detailState.exporting}>
                            Offizielles Dokument
                          </Button>
                        ) : null}
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">{formatFillabilityClass(entry.fillability_class)}</span>
                      </div>
                    </div>
                    <FilingDisclosure
                      title="Dokumentdetails"
                      subtitle="Fillability, Felder, Warnhinweise, Bibliotheksstand und technische Ableitung nur bei Bedarf."
                    >
                      <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
                        <div className="space-y-3">
                          <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                            <p>Status: {entry.checklist_status?.status || "unbekannt"}</p>
                            <p>Befuellbarkeit: {formatFillabilityClass(entry.fillability_class)} · Abdeckung {formatCoverageLevel(entry.field_coverage_level)} · Sicherheit {entry.fillability_confidence ?? "k.A."}</p>
                            <p>PDF-Felder {entry.pdf_field_count ?? 0} · Auto {entry.autofill_field_count ?? 0} · Manuell {entry.manual_field_count ?? 0}</p>
                            <p>Letzter Download {entry.downloaded_at || "noch nicht lokal"} · Letzte Pruefung {entry.last_checked_at || "k.A."}</p>
                          </div>
                          {entry.plausibility_warnings?.length ? (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <p className="text-sm font-semibold text-amber-800">Plausibilitaetshinweise</p>
                              <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                                {entry.plausibility_warnings.map((note) => <li key={note}>{note}</li>)}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                        <div className="space-y-3">
                          {entry.review_requirements?.length ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-sm font-semibold text-slate-900">Pruefkriterien</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {entry.review_requirements.map((requirement) => (
                                  <span key={`${entry.id}-${requirement}`} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                                    {requirement}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          {entry.freshness ? (
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-sm font-semibold text-slate-900">Dokumentenstand</p>
                              <p className="mt-2 text-sm text-slate-700">{entry.freshness.label}</p>
                              <span className={`mt-2 inline-flex rounded-full border px-2 py-1 text-[11px] ${getFreshnessMeta(entry.freshness.freshness_status).tone}`}>{entry.freshness.label}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </FilingDisclosure>
                  </div>
                );
              })}
            </div>
          </div>

          {backgroundReferenceDocuments.length ? (
            <FilingDisclosure
              title="Leitfaeden & Referenzen"
              subtitle="Allgemeine Drittstaat-, Visa- und Hintergrundunterlagen bleiben verfuegbar, zaehlen aber nicht als operative Pflichtdokumente dieses Falls."
            >
              <div className="space-y-3">
                {backgroundReferenceDocuments.map((entry) => (
                  <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{entry.title}</p>
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">Hintergrundreferenz</span>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          {entry.fillability_reason || entry.notes || entry.authority || "Allgemeine Hintergrundunterlage fuer Einordnung und Pruefung."}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                          Diese Unterlage dient nur der Orientierung und ist nicht Teil der operativen Pflichtdokumente fuer diesen Fall.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {entry.download_url ? (
                          <Button variant="outline" className="dashboard-inline-button" onClick={() => onDownloadAuthorityDocument(row.id, entry)} disabled={detailState.exporting}>
                            Referenz oeffnen
                          </Button>
                        ) : null}
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">{formatFillabilityClass(entry.fillability_class)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </FilingDisclosure>
          ) : null}

          <FilingDisclosure
            title="Behoerden & Zustaendigkeiten"
            subtitle="Foederale Aufloesung, Behoerdenliste und Kontaktblock bleiben erhalten, aber als Sekundaerebene."
          >
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-sm font-semibold text-slate-900">Foederale Aufloesung</p>
                <p className="mt-2 text-sm text-slate-600">{filingData.jurisdiction_resolution?.summary}</p>
                <div className="mt-3 grid gap-3 xl:grid-cols-3">
                  {(filingData.jurisdiction_resolution?.layers || []).map((layer) => (
                    <div key={layer.level} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{layer.level}</p>
                      <p className="mt-2 font-semibold text-slate-900">{layer.title}</p>
                      <p className="mt-1 text-sm text-slate-600">{layer.effect}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">Behoerdenliste</p>
                  <div className="mt-3 space-y-3">
                    {(filingData.authority_matrix || []).map((entry) => (
                      <div key={`${entry.title}-${entry.level}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="font-semibold text-slate-900">{entry.title}</p>
                        <p className="text-sm text-slate-600">{entry.scope}</p>
                        <p className="mt-1 text-xs text-slate-500">{entry.resolved_state || "Bund"} · {entry.resolved_city || "ohne Ortsbindung"}</p>
                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-sky-700">Quelle oeffnen</a>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">Kontaktblock</p>
                  <div className="mt-3 space-y-3">
                    {(filingData.employer_ready_pack?.authority_contact_block || []).map((entry) => (
                      <div key={`${entry.title}-${entry.city || entry.state}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="font-semibold text-slate-900">{entry.title}</p>
                        <p className="mt-1 text-sm text-slate-600">{entry.state}{entry.city ? ` / ${entry.city}` : ""}</p>
                        <a href={entry.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-sky-700">Offizielle Quelle</a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FilingDisclosure>

          <FilingDisclosure
            title="Dokumentenstand & Aktualitaet"
            subtitle="Bibliotheksfrische, letzte Aktualisierung und Unsicherheiten bleiben erhalten, aber nur als Detailbereich."
          >
            <div className="space-y-4">
              {filingData.document_refresh ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                  Letzte Bibliotheksaktualisierung: {filingData.document_refresh.updated_count} aktualisiert, {filingData.document_refresh.skipped_count} uebersprungen, {filingData.document_refresh.error_count} Fehler.
                </div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Lokal geprueft</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.current_locally_checked_documents || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaet unklar</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.local_unknown_currency_documents || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Nur beschrieben</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.officially_described_documents || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Refresh-faehig</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{filingData.document_library_status?.refreshable_documents || 0}</p>
                </div>
              </div>
            </div>
          </FilingDisclosure>
        </TabsContent>

        <TabsContent value="pack" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Kontrollierte Pack-Reife</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">
                  {currentPack ? `Pack ${currentPack.pack_no} · ${currentPack.stage_label || currentPack.stage}` : (filingData.submission_pack_state?.current_stage_label || "Arbeitsstand")}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Arbeitsstand {filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"}
                  {" · "}
                  Freeze {currentPack ? (currentPack.freeze_state || "eingefroren") : "noch keiner"}
                  {currentPack?.is_stale ? " · seit Freeze veraltet" : ""}
                </p>
              </div>
              <div className="flex flex-col gap-2 lg:items-end">
                <CLoadingButton
                  className="dashboard-action-button"
                  disabledOnLoading
                  loading={detailState.finalizingPack}
                  onClick={() => onFinalizeSubmissionPack(row.id)}
                  disabled={!(packOps.gates?.can_freeze_for_review || packOps.gates?.can_refreeze)}
                >
                  {detailState.finalizingPack ? "Pack wird eingefroren..." : (packOps.gates?.can_refreeze ? "Neu einfrieren" : "Fuer Pruefung einfrieren")}
                </CLoadingButton>
                <div className="flex flex-wrap gap-2">
                  {packSecondaryActions.map((action) => (
                    <CLoadingButton
                      key={action.key}
                      variant="outline"
                      className="dashboard-inline-button"
                      disabledOnLoading
                      loading={detailState.finalizingPack}
                      onClick={action.onClick}
                      disabled={!action.enabled || detailState.filingLoading}
                    >
                      {action.label}
                    </CLoadingButton>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="dashboard-inline-button" onClick={() => onExportKit(row.id, "html")} disabled={detailState.exporting || detailState.finalizingPack || detailState.filingLoading}>
                    Arbeitsansicht exportieren
                  </Button>
                  <CLoadingButton
                    color="danger"
                    variant="outline"
                    className="dashboard-inline-button"
                    disabledOnLoading
                    loading={detailState.finalizingPack}
                    onClick={packRiskAction.onClick}
                    disabled={!packRiskAction.enabled || detailState.filingLoading}
                  >
                    {packRiskAction.label}
                  </CLoadingButton>
                </div>
              </div>
            </div>
            <CCallout className="mt-4 mb-0" color={workspacePrimaryAction ? "info" : "warning"}>
              <strong>Gate / Pack Handoff</strong>
              <div>
                Aktueller Pack-Stand: {filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · groesster Blocker: {packDominantBlocker?.title || "kein dominanter Blocker"}.
              </div>
              <div className="mt-2">
                {workspacePrimaryAction
                  ? `Naechster zulaessiger Schritt: ${workspacePrimaryAction.label}. ${packActionHierarchyHint}`
                  : packActionHierarchyHint}
              </div>
              <div className="mt-2">
                {packSecondaryActions.filter((action) => !action.enabled).slice(0, 2).map((action) => action.reason).join(" ")
                  || (!packRiskAction.enabled ? packRiskAction.reason : "Sekundaer- und Risikoaktionen bleiben sichtbar nachgeordnet und werden nur im passenden Gate-Zustand aktiv.")}
              </div>
            </CCallout>
            <CCallout className="mt-4 mb-0" color={packReadbackTone}>
              <strong>State Transition Readback</strong>
              <div>{packReadbackSummary}</div>
              <div className="mt-2">
                {packDominantBlocker
                  ? `Blockiert oder gebremst aktuell durch: ${packDominantBlocker.detail || packDominantBlocker.title}`
                  : `Naechster zulaessiger Schritt: ${workspacePrimaryAction?.label || packNextAction?.label || "aktuellen Pack-Stand weiterfuehren"}`}
              </div>
            </CCallout>
            {trainingGuidance && (trainingRequest || trainingTask || openCandidateRequest) ? (
              <CCallout className="mt-4 mb-0" color="warning">
                <p className="text-xs uppercase tracking-[0.14em] text-amber-800">Fehlenden Nachweis anfordern</p>
                <p className="mt-2 font-semibold text-slate-900">Diese Aktion passiert nicht im Pack, sondern im Bereich Kandidatenanforderungen.</p>
                <p className="mt-1 text-sm text-amber-950">
                  Wechsle zuerst zu <strong>Kandidatenanforderungen</strong>. Dort legst du die Nachforderung an, sendest sie oder pruefst die eingegangene Rueckmeldung.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" className="dashboard-inline-button" onClick={() => onTabChange("candidate")}>
                    Zu den Kandidatenanforderungen
                  </Button>
                  {trainingPrimaryAction ? (
                    <CLoadingButton className="dashboard-action-button" disabledOnLoading loading={detailState.filingLoading} disabled={detailState.finalizingPack} onClick={trainingPrimaryAction.onClick}>
                      {trainingPrimaryAction.label}
                    </CLoadingButton>
                  ) : null}
                </div>
              </CCallout>
            ) : null}
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktueller Arbeitsstand</p>
                <p className="mt-2 font-semibold text-slate-900">{packOps.workspace_stage_label || filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Freeze-Status</p>
                <p className="mt-2 font-semibold text-slate-900">{currentPack ? (currentPack.freeze_state || "eingefroren") : "noch kein Freeze"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Aktualitaetsstatus</p>
                <p className="mt-2 font-semibold text-slate-900">{currentPack?.is_stale ? "veraltet seit Freeze" : "stabiler Freeze-Stand"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechste Aktion</p>
                <p className="mt-2 font-semibold text-slate-900">{packNextAction?.label || "Weiter bearbeiten"}</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 xl:grid-cols-[1.1fr,0.9fr]">
              <CCallout color="warning" className="mb-0">
                <p className="text-xs uppercase tracking-[0.14em] text-amber-800">Groesster Blocker</p>
                <p className="mt-2 font-semibold text-slate-900">{packDominantBlocker?.title || "Kein dominanter Blocker"}</p>
                <p className="mt-1 text-sm text-amber-900">{packDominantBlocker?.detail || packDominantBlocker?.description || filingData.submission_pack_state?.stage_reason || "Der Pack-Stand ist aktuell stabil."}</p>
              </CCallout>
              <CCallout color="info" className="mb-0">
                <p className="text-xs uppercase tracking-[0.14em] text-sky-800">Warum jetzt?</p>
                <p className="mt-2 font-semibold text-slate-900">{packNextAction?.label || "Weiter im Arbeitsmodus"}</p>
                <p className="mt-1 text-sm text-sky-900">{packNextAction?.detail || "Erst die verbleibenden Filing-Punkte sauber schliessen."}</p>
              </CCallout>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Aktuell relevantes Paket</p>
              {currentPack ? (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">Pack {currentPack.pack_no} · {currentPack.stage_label || currentPack.stage}</p>
                    <span className="text-xs text-slate-500">{currentPack.last_acted_at || currentPack.created_at}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    Freeze {currentPack.freeze_state || "eingefroren"}
                    {currentPack.is_stale ? ` · ${currentPack.stale_reason_summary || "seit Freeze veraltet"}` : " · stabil"}
                  </p>
                  {packOps.freeze_delta_summary?.length ? (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3">
                      <p className="text-xs uppercase tracking-[0.14em] text-amber-700">Seit Freeze geaendert</p>
                      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                        {packOps.freeze_delta_summary.map((line) => <li key={line}>{line}</li>)}
                      </ul>
                    </div>
                  ) : null}
                  {!!currentPack.history?.length && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Pack-Historie</p>
                      <div className="mt-2 space-y-2">
                        {currentPack.history.slice(0, 5).map((entry) => (
                          <div key={`${entry.action}-${entry.acted_at}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="text-sm font-semibold text-slate-900">{formatHistoryAction(`pack_${entry.action}`)}</p>
                            <p className="mt-1 text-xs text-slate-500">{formatDateTime(entry.acted_at)} · {entry.acted_by || "system"}</p>
                            {entry.stale_reason_summary ? <p className="mt-1 text-xs text-amber-700">{entry.stale_reason_summary}</p> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(currentPack.pack_assets || latestPack?.pack_assets || []).map((asset) => (
                      <Button key={`pack-asset-${asset.asset_id}`} variant="outline" className="dashboard-inline-button" onClick={() => onDownloadPreparedAsset(row.id, asset)} disabled={detailState.exporting}>
                        {asset.file_name}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-600">Noch kein kontrollierter Freeze-Stand vorhanden.</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Readiness Gates</p>
              <div className="mt-3 space-y-3">
                {(packOps.blockers || []).slice(0, 5).map((entry) => (
                  <CCallout key={`blocker-${entry.code}`} color="danger" className="mb-0">
                    <strong>{entry.title}</strong> · {entry.detail}
                  </CCallout>
                ))}
                {(packOps.warnings || []).slice(0, 5).map((entry) => (
                  <CCallout key={`warning-${entry.code}`} color="warning" className="mb-0">
                    <strong>{entry.title}</strong> · {entry.detail}
                  </CCallout>
                ))}
                {!packOps.blockers?.length && !packOps.warnings?.length ? (
                  <CCallout color="success" className="mb-0">
                    Keine harten Release-Blocker aktiv. Der Pack-Stand kann kontrolliert weitergefuehrt werden.
                  </CCallout>
                ) : null}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verlauf / Historie</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">Aenderungen, Versionen und Vergleich bleiben verfuegbar, aber als Analyseebene statt als Hauptbuehne.</p>
                <p className="mt-1 text-sm text-slate-600">Standardansicht reduziert, Details nur noch bei Bedarf.</p>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Letzte Version {latestHistory?.version_no || 0}
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <CCallout color="info" className="mb-4">
              <strong>History Queue</strong>
              <div>Diese Liste ist die schnelle Review-Ebene fuer Versionen und Pack-Verlauf. Tieferer Compare- und Delta-Kontext bleibt darunter erhalten.</div>
            </CCallout>
            {filingHistoryRows.length ? (
              <>
                <CSmartTable
                  columns={[
                    { key: "version", label: "Version" },
                    { key: "action", label: "Aktion" },
                    { key: "stage", label: "Pack-Stufe" },
                    { key: "openPoints", label: "Offene Punkte" },
                    { key: "packs", label: "Finale Packs" },
                    { key: "createdAt", label: "Erstellt" },
                    { key: "detail", label: "Detail", sorter: false, filter: false },
                  ]}
                  items={pagedHistoryQueueRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    detail: (item) => (
                      <td>
                        <Button
                          variant={String(activeHistoryRow?.id || "") === String(item.id) ? "default" : "outline"}
                          className="dashboard-inline-button"
                          onClick={() => setSelectedHistoryId(String(item.id))}
                        >
                          {String(activeHistoryRow?.id || "") === String(item.id) ? "Aktiv" : "Details"}
                        </Button>
                      </td>
                    ),
                  }}
                />
                {filingHistoryRows.length > historyQueuePageSize ? (
                  <div className="mt-3 flex justify-end">
                    <CSmartPagination
                      activePage={historyQueuePage}
                      align="end"
                      dots
                      doubleArrows
                      onActivePageChange={setHistoryQueuePage}
                      pages={historyQueuePageCount}
                    />
                  </div>
                ) : null}
                {activeHistoryRow ? (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">History Detail</p>
                        <p className="mt-2 text-lg font-semibold text-slate-900">{activeHistoryRow.version} · {activeHistoryRow.action}</p>
                        <p className="mt-1 text-sm text-slate-600">
                          Pack-Stufe {activeHistoryRow.stage} · erstellt {activeHistoryRow.createdAt}
                        </p>
                      </div>
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">{activeHistoryRow.openPoints}</span>
                    </div>
                    <CCallout color={activeHistoryRow.entry?.history_summary?.current_pack_is_stale ? "warning" : "info"} className="mt-4 mb-0">
                      <strong>History Handoff</strong>
                      <div>
                        Damals: Freeze {activeHistoryRow.entry?.history_summary?.current_pack_freeze_state || "k.A."}
                        {activeHistoryRow.entry?.history_summary?.current_pack_is_stale ? " · Stand war zu diesem Zeitpunkt bereits veraltet." : " · Stand war zum Snapshot belastbar."}
                      </div>
                      <div className="mt-2">
                        Heute: {filingData.submission_pack_state?.current_stage_label || "Arbeitsstand"} · naechster Blickpunkt {packNextAction?.label || "aktuellen Gate-Stand spiegeln"}
                      </div>
                    </CCallout>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {historyCompareItems.map((item) => (
                        <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-3">
                          <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                          <p className="mt-2 font-semibold text-slate-900">{item.primary}</p>
                          <p className="mt-1 text-sm text-slate-600">{item.secondary}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Finale Packs</p>
                        <p className="mt-2 text-sm text-slate-900">{activeHistoryRow.packs}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Blocker / Warnungen</p>
                        <p className="mt-2 text-sm text-slate-900">
                          {activeHistoryRow.entry?.history_summary?.blocking_count || 0} Blocker · {activeHistoryRow.entry?.history_summary?.warning_count || 0} Warnungen
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Compare</p>
                        <p className="mt-2 text-sm text-slate-900">
                          {activeHistoryRow.entry?.previous_version_delta?.summary_lines?.[0] || "Kein Delta gegen die Vorversion hinterlegt."}
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechster Blick</p>
                        <p className="mt-2 text-sm text-slate-900">
                          {activeHistoryRow.entry?.history_summary?.current_pack_is_stale ? "Aktuelle Queue gegen die veraltete Version abgleichen." : "Version mit dem aktuellen Gate-Stand vergleichen."}
                        </p>
                      </div>
                    </div>
                    {activeHistoryRow.entry?.previous_version_delta?.summary_lines?.length ? (
                      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                        <ul className="list-disc pl-5 text-sm text-slate-700">
                          {activeHistoryRow.entry.previous_version_delta.summary_lines.map((line) => <li key={line}>{line}</li>)}
                        </ul>
                      </div>
                    ) : null}
                    <CCallout color={activeHistoryRow.entry?.history_summary?.current_pack_is_stale ? "warning" : "info"} className="mt-4 mb-0">
                      <strong>History Readback</strong>
                      <div>
                        Damals war der Stand {activeHistoryRow.entry?.history_summary?.current_pack_is_stale ? "nicht mehr stabil" : "belastbar"}; heute bleibt der relevante Anschlussblick {packNextAction?.label || "den aktuellen Workspace-Stand zu spiegeln"}.
                      </div>
                    </CCallout>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-slate-600">Noch keine gespeicherte Filing-Historie vorhanden.</p>
            )}
          </div>

          <FilingDisclosure
            title="Aenderungen seit letzter Version"
            subtitle="Version Compare bleibt erhalten, aber kompakt und einklappbar."
            defaultOpen
          >
            {filingData.version_compare?.summary_lines?.length ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <ul className="list-disc pl-5 text-sm text-slate-700">
                    {filingData.version_compare.summary_lines.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Dokumente</p>
                    <p className="mt-2 text-sm text-slate-900">Neu {formatDelta(filingData.version_compare.document_changes?.added?.length || 0)} · Entfallen {formatDelta(-1 * (filingData.version_compare.document_changes?.removed?.length || 0))}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Offene Punkte</p>
                    <p className="mt-2 text-sm text-slate-900">Aufgaben {formatDelta(filingData.version_compare.open_tasks_delta || 0)} · Anfragen {formatDelta(filingData.version_compare.active_requests_delta || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Reifegrad</p>
                    <p className="mt-2 text-sm text-slate-900">{filingData.version_compare.previous_pack_label || "k.A."} -> {filingData.version_compare.current_pack_label || "aktuell"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zustellung</p>
                    <p className="mt-2 text-sm text-slate-900">Mails {formatDelta(filingData.version_compare.email_sent_delta || 0)} · Fehler {formatDelta(filingData.version_compare.delivery_failure_delta || 0)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Release Ops</p>
                    <p className="mt-2 text-sm text-slate-900">
                      Pack-Stufe {filingData.version_compare.previous_pack_stage || "k.A."} -> {filingData.version_compare.current_pack_stage || "k.A."}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Freeze {filingData.version_compare.previous_freeze_state || "k.A."} -> {filingData.version_compare.current_freeze_state || "k.A."} ·
                      Blocker {formatDelta(filingData.version_compare.blocker_delta || 0)} ·
                      Warnungen {formatDelta(filingData.version_compare.warning_delta || 0)} ·
                      Neu eingefroren {formatDelta(filingData.version_compare.refreeze_delta || 0)}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600">Sobald eine Vorversion existiert, erscheint hier der reduzierte Vergleich zum aktuellen Stand.</p>
            )}
          </FilingDisclosure>

          <FilingDisclosure
            title="Versionen und Verlauf"
            subtitle="Persistente Historie, finale Pakete und aeltere Staende in komprimierter Form."
          >
            <div className="space-y-3">
              {filingData.history?.length ? filingData.history.map((entry) => (
                <div key={`${entry.case_id}-${entry.version_no}`} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">Version {entry.version_no} · {formatHistoryAction(entry.action_type)}</p>
                    <span className="text-xs text-slate-500">{entry.created_at}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {entry.classification?.procedure_explainer?.label || entry.classification?.procedure || "Pfad"} · {entry.classification?.federal_state || "ohne Bundesland"} · {entry.classification?.city || "ohne Stadt"}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Offene Aufgaben {entry.history_summary?.open_tasks_count || 0} · aktive Anfragen {entry.history_summary?.active_requests_count || 0} · finale Pakete {entry.history_summary?.final_pack_count || 0}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Pack-Stufe {entry.history_summary?.current_pack_stage || "k.A."} · Freeze {entry.history_summary?.current_pack_freeze_state || "k.A."}{entry.history_summary?.current_pack_is_stale ? " · veraltet" : ""}
                  </p>
                  {entry.previous_version_delta?.summary_lines?.length ? (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <ul className="list-disc pl-5 text-sm text-slate-700">
                        {entry.previous_version_delta.summary_lines.slice(0, 4).map((line) => <li key={`${entry.version_no}-${line}`}>{line}</li>)}
                      </ul>
                    </div>
                  ) : null}
                </div>
              )) : (
                <p className="text-sm text-slate-600">Noch keine gespeicherte Historie vorhanden.</p>
              )}
            </div>
          </FilingDisclosure>
        </TabsContent>
      </Tabs>
      <COffcanvas
        placement="end"
        visible={Boolean(activeRequestRow) && requestDrawerVisible}
        onHide={() => setRequestDrawerVisible(false)}
        style={{ "--cui-offcanvas-width": "min(760px, 100vw)" }}
      >
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>Request-Details & persoenliches Schreiben</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          {activeRequestRow ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Aktive Nachforderung</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{activeRequestRow.requestEntry.title}</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {activeRequestRow.status} · Frist {activeRequestRow.dueAt} · letzte Aktivitaet {activeRequestRow.lastActivity}
                    </p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs ${activeRequestRow.tone}`}>{activeRequestRow.bucket}</span>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Request-Grund</p>
                    <p className="mt-2 text-sm text-slate-700">{activeRequestRow.requestEntry.request_reason || activeRequestRow.requestEntry.description || "Keine Begruendung hinterlegt."}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zustellung / Reminder</p>
                    <p className="mt-2 text-sm text-slate-700">
                      Letzte Mail {formatDateTime(activeRequestRow.requestEntry.delivery_summary?.last_email_delivery_at) || "Noch kein Versand"} · Reminder {activeRequestRow.requestEntry.reminder_count || 0}
                    </p>
                  </div>
                </div>
              </div>

              <CCallout color="info" className="mb-0">
                <strong>Was der Klick auf Details jetzt zeigt</strong>
                <div>Hier oeffnet sich die echte Bearbeitungsflaeche fuer diese Nachforderung: Status, Frist, Versandstand und das Schreiben an den Bewerber.</div>
              </CCallout>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <CFormLabel className="text-xs uppercase tracking-[0.18em] text-slate-400">Betreff fuer den Bewerber</CFormLabel>
                <CFormInput
                  className="mt-2"
                  value={activeRequestDraft.subject}
                  onChange={(event) => patchRequestDraft(activeRequestRow.requestEntry.request_id, { subject: event.target.value })}
                  placeholder="Betreff der persoenlichen Nachforderung"
                />
                <p className="mt-2 text-xs text-slate-500">Der Betreff wird fuer Mail und Kandidatenansicht verwendet.</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Persoenliches Schreiben</p>
                    <p className="mt-1 text-sm text-slate-600">Du kannst hier frei formulieren und formatieren: fett, unterstreichen, Listen und Links.</p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                    Vorschau fuer Mail und Kandidatenansicht
                  </span>
                </div>
                <div className="mt-3 [&_.ql-container]:min-h-[220px] [&_.ql-editor]:min-h-[220px] [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:border-slate-200 [&_.ql-container]:border-slate-200">
                  <ReactQuill
                    theme="snow"
                    modules={REQUEST_EDITOR_MODULES}
                    formats={REQUEST_EDITOR_FORMATS}
                    value={activeRequestDraft.html}
                    onChange={(value) => patchRequestDraft(activeRequestRow.requestEntry.request_id, { html: value })}
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Schnellvorschau</p>
                <p className="mt-2 font-semibold text-slate-900">{activeRequestDraft.subject || "Ohne Betreff"}</p>
                <div
                  className="prose prose-slate mt-3 max-w-none rounded-lg border border-slate-200 bg-white p-4 text-sm"
                  dangerouslySetInnerHTML={{ __html: normalizeRequestMessageHtml(activeRequestDraft.html) || "<p>Kein Schreiben hinterlegt.</p>" }}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <CLoadingButton
                  variant="outline"
                  className="dashboard-inline-button"
                  disabledOnLoading
                  loading={detailState.filingLoading}
                  onClick={() => saveCandidateRequestDraft(activeRequestRow.requestEntry)}
                >
                  Entwurf speichern
                </CLoadingButton>
                {activeRequestRow.requestEntry.status === "draft" ? (
                  <CLoadingButton
                    className="dashboard-action-button"
                    disabledOnLoading
                    loading={detailState.filingLoading}
                    onClick={() => saveCandidateRequestDraft(activeRequestRow.requestEntry, "sent")}
                  >
                    Schreiben jetzt senden
                  </CLoadingButton>
                ) : null}
                {canSendCandidateReminder(activeRequestRow.requestEntry) ? (
                  <CLoadingButton
                    variant="outline"
                    className="dashboard-inline-button"
                    disabledOnLoading
                    loading={detailState.filingLoading}
                    onClick={() => saveCandidateRequestDraft(activeRequestRow.requestEntry, activeRequestRow.requestEntry.status, { reminder: true })}
                  >
                    {getCandidateReminderLabel(activeRequestRow.requestEntry)}
                  </CLoadingButton>
                ) : null}
              </div>
            </div>
          ) : null}
        </COffcanvasBody>
      </COffcanvas>
    </div>
  );
}
